// DB module format
export interface FirewallModule {
  settings: FirewallSettingsSection;
  network: FirewallNetworkSection;
  advanced: FirewallAdvancedSection;
}

export interface FirewallSettingsSection {
  enable: boolean;
  blockPortScan: boolean;
  allowInternetConnectionSharing: boolean;
  monitorWiFiConnections: boolean;
  logVerbosity: {
    enable: boolean;
    level: FirewallLogVerbosityLevel;
  };
  instructionsDetectionSystem: {
    enable: boolean;
    profile: FirewallIdsProfileType;
  };
  exclusions: {
    enable: boolean;
    duplicateToNetworkProtection: boolean;
    items: FirewallExclusionItem[];
  };
}

export interface FirewallNetworkSection {
  adapters: FirewallNetworkAdapterItem[];
  networks?: FirewallNetworkItem[];
}

export interface FirewallAdvancedSection {
  protectionLevel: FirewallAdvancedProtectionLevel;
  createAgresiveRules: boolean;
  createRulesForAppsBlockedByIDS: boolean;
  monitorProcessChanges: boolean;
  ignoreSignedProcesses: boolean;
  rule: FirewallAdvancedRule[];
}

export interface FirewallExclusionItem {
  ip: string;
  description: string;
}

export interface FirewallNetworkAdapterItem {
  type: FirewallAdapterType;
  networkType: FirewallAdapterNetworkType;
  stealthMode: FirewallAdapterStealthMode;
}

export interface FirewallNetworkItem {
  network: string;
  type: FirewallNetworkType;
  identification: FirewallNetworkIdentification;
  domainMac: string;
  ip: string;
}

export interface FirewallAdvancedRule {
  ruleId: string;
  defaultRule: FirewallAdvancedDefaultRule;
  ruleType: FirewallAdvancedRuleType;
  type: number;
  enabled?: boolean;
  details: FirewallAdvancedRuleDetails;
  settings: FirewallAdvancedRuleSettings;
  permission: FirewallAdvancedRulePermission;
}

export interface FirewallAdvancedRuleFormValue extends FirewallAdvancedRule {
  defaultPositionInGrid: number;
}

interface FirewallAdvancedRuleDetails {
  name: string;
  applictionPath: string;
  commandLine: string;
  applicationMd5: string;
}
interface FirewallAdvancedRuleSettings {
  localAddress: {
    any: boolean;
    ipMask?: string;
    portRange: string;
  };
  remoteAddress: {
    any: boolean;
    ipMask?: string;
    portRange: string;
  };
  directlyConnected: {
    enable: boolean;
    remoteMac?: string;
  };
  protocol: FirewallAdvancedRuleProtocolType;
  customProtocol: FirewallAdvancedRuleCustomProtocolType;
  direction: FirewallAdvancedRuleSettingsDirection;
  ipVersion: FirewallAdvancedRuleSettingsIpVersion;
}
interface FirewallAdvancedRulePermission {
  home: boolean;
  public: boolean;
  setPermission: FirewallAdvancedRulePermissionType;
}

export enum FirewallNetworkType {
  TRUSTED = 1,
  HOME_OFFICE = 2,
  PUBLIC = 3,
  UNTRUSTED = 4,
}

export enum FirewallAdapterType {
  WIRED = 'Wired',
  WIRELESS = 'Wireless',
  VIRTUAL = 'Virtual',
}

export enum FirewallAdapterNetworkType {
  TRUSTED = 1,
  HOME_OFFICE = 2,
  PUBLIC = 3,
  UNTRUSTED = 4,
  LET_WINDOWS_DECIDE = 5,
}

export enum FirewallAdapterStealthMode {
  ON = 1,
  OFF = 2,
  REMOTE = 3,
}

export enum FirewallNetworkIdentification {
  DNS = 1,
  GATEWAY = 2,
  NETWORK = 3,
}

export enum FirewallLogVerbosityLevel {
  LOW = 1,
  MEDIUM = 2,
  HIGH = 3,
}

export enum FirewallIdsProfileType {
  AGGRESSIVE = 1,
  NORMAL = 2,
  PERMISSIVE = 3,
}

export enum FirewallAdvancedProtectionLevel {
  RULESET_AND_ALLOW = 1,
  RULESET_AND_ASK = 2,
  RULESET_AND_DENY = 3,
  RULESET_KNOWN_FILES_AND_ALLOW = 4,
  RULESET_KNOWN_FILES_AND_ASK = 5,
  RULESET_KNOWN_FILES_AND_DENY = 6,
}

export enum FirewallAdvancedDefaultRule {
  YES = 1,
  NO = 0,
}

export enum FirewallAdvancedRuleType {
  APPLICATION = 1,
  CONNECTION = 2,
}

export enum FirewallAdvancedRuleProtocolType {
  ANY = 1,
  TCP = 2,
  UDP = 3,
  CUSTOM = 4,
}

export enum FirewallAdvancedRuleCustomProtocolType {
  // 'HOPOPT' = '0',
  'ICMP' = '1',
  'IGMP' = '2',
  'GGP' = '3',
  // IPV4 = "4",
  'ST' = '5',
  // TCP = "6",
  'CBT' = '7',
  'EGP' = '8',
  'IGP' = '9',
  'BBN-RCC-MON' = '10',
  'NVP-II' = '11',
  'PUP' = '12',
  'ARGUS' = '13',
  'EMCON' = '14',
  'XNET' = '15',
  'CHAOS' = '16',
  // UDP = "17",
  'MUX' = '18',
  'DCN-MEAS' = '19',
  'HMP' = '20',
  'PRM' = '21',
  'XNS-IDP' = '22',
  'TRUNK-1' = '23',
  'TRUNK-2' = '24',
  'LEAF-1' = '25',
  'LEAF-2' = '26',
  'RDP' = '27',
  'IRTP' = '28',
  'ISO-TP4' = '29',
  'NETBLT' = '30',
  'MFE-NSP' = '31',
  'MERIT-INP' = '32',
  'DCCP' = '33',
  '3PC' = '34',
  'IDPR' = '35',
  'XTP' = '36',
  'DDP' = '37',
  'IDPR-CMTP' = '38',
  'TP++' = '39',
  'IL' = '40',
  // 'IPv6' = '41',
  'SDRP' = '42',
  'IPv6-Route' = '43',
  'IPv6-Frag' = '44',
  'IDRP' = '45',
  'RSVP' = '46',
  'GRE' = '47',
  'MHRP' = '48',
  'BNA' = '49',
  'ESP' = '50',
  'AH' = '51',
  'I-NLSP' = '52',
  'SWIPE' = '53',
  'NARP' = '54',
  'MOBILE' = '55',
  'TLSP' = '56',
  'SKIP' = '57',
  'IPv6-ICMP' = '58',
  'IPv6-NoNxt' = '59',
  'IPv6-Opts' = '60',
  // '' = '61'
  'CFTP' = '62',
  // '' = '63'
  'SAT-EXPAK' = '64',
  'KRYPTOLAN' = '65',
  'RVD' = '66',
  'IPPC' = '67',
  // '' = '68'
  'SAT-MON' = '69',
  'VISA' = '70',
  'IPCV' = '71',
  'CPNX' = '72',
  'CPHB' = '73',
  'WSN' = '74',
  'PVP' = '75',
  'BR-SAT-MON' = '76',
  'SUN-ND' = '77',
  'WB-MON' = '78',
  'WB-EXPAK' = '79',
  'ISO-IP' = '80',
  'VMTP' = '81',
  'SECURE-VMTP' = '82',
  'VINES' = '83',
  'IPTM' = '84',
  'NSFNET-IGP' = '85',
  'DGP' = '86',
  'TCF' = '87',
  'EIGRP' = '88',
  'OSPF' = '89',
  'Sprite-RPC' = '90',
  'LARP' = '91',
  'MTP' = '92',
  'AX.25' = '93',
  'IPIP' = '94',
  'MICP' = '95',
  'SCC-SP' = '96',
  'ETHERIP' = '97',
  'ENCAP' = '98',
  // '' = '99'
  'GMTP' = '100',
  'IFMP' = '101',
  'PNNI' = '102',
  'PIM' = '103',
  'ARIS' = '104',
  'SCPS' = '105',
  'QNX' = '106',
  'A/N' = '107',
  'IPComp' = '108',
  'SNP' = '109',
  'Compaq-Peer' = '110',
  'IPX-in-IP' = '111',
  'VRRP' = '112',
  'PGM' = '113',
  // '' = '114'
  'L2TP' = '115',
  'DDX' = '116',
  'IATP' = '117',
  'STP' = '118',
  'SRP' = '119',
  'UTI' = '120',
  'SMP' = '121',
  'SM' = '122',
  'PTP' = '123',
  // '' = '124'
  'FIRE' = '125',
  'CRTP' = '126',
  'CRUDP' = '127',
  'SSCOPMCE' = '128',
  'IPLT' = '129',
  'SPS' = '130',
  'PIPE' = '131',
  'SCTP' = '132',
  'FC' = '133',
  // '' = '134'
  // '' = '135'
  // '' = '136'
  // '' = '137'
  'MANET' = '138',
  'HIP' = '139',
  'Shim6' = '140',
}

export enum FirewallAdvancedRulePermissionType {
  ALLOW = 1,
  DENY = 2,
}

export enum FirewallAdvancedRuleSettingsDirection {
  BOTH = 1,
  INBOUND = 2,
  OUTBOUND = 3,
}

export enum FirewallAdvancedRuleSettingsIpVersion {
  ANY = 1,
  IPV4 = 2,
  IPV6 = 3,
}
